import {
  FluidFormElementState,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import { FluidFormElementTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-form-element-translation-options.interface';
import { FluidDate } from '@lmig/fluid-core/lib/structs/types/fluid-date.type';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
} from '@stencil/core';
import { isAfter, parseISO } from 'date-fns';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../utils/events/event-utils';
import { Utils } from '../../utils/utils';
import {
  DateRange,
  DateRangeField,
  InvalidDateMessage,
} from './components/fluid-date-range.components';

/**
 * @displayName Date Range
 */
@Component({
  tag: 'fluid-date-range',
  styleUrl: 'fluid-date-range.scss',
  shadow: true,
})
export class FluidDateRange implements ComponentInterface, FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidDateRangeElement;

  // ================================================================ //
  // -- States

  @State() _fromDate: string | Date | undefined;

  @State() _toDate: string | Date | undefined;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * The minimum selectable date for the 'from' range.
   */
  @Prop({ mutable: true }) minDate: FluidDate;

  /**
   * The maximum selectable date for the 'to' range.
   */
  @Prop({ mutable: true }) maxDate: FluidDate;

  /**
   * Allows a unique identifier to be passed to be prepended to test IDs
   */
  @Prop({ mutable: true }) identifier: string;

  /**
   * Allows you to set the 'From' label
   * @default 'From'
   */
  @Prop({ mutable: true }) fromLabel = 'From';

  /**
   * Allows you to set the 'To' label
   * @default 'To'
   */
  @Prop({ mutable: true }) toLabel = 'To';

  /**
   * Allows you to customise the 'From' data path to suit your data model
   * @default 'from'
   */
  @Prop({ mutable: true }) fromDataPath = 'from';

  /**
   * Allows you to customise the 'To' data path to suit your data model
   * @default 'to'
   */
  @Prop({ mutable: true }) toDataPath = 'to';

  /**
   * Optionally set the initial value.
   */
  @Prop({ mutable: true }) initialValue?: DateRange;

  // ================================================================ //
  // -- Events

  @Event() valueChange: EventEmitter;

  // ================================================================ //
  // -- Internal State
  _fromElement: HTMLFluidDatepickerElement;
  _toElement: HTMLFluidDatepickerElement;

  @State() _invalidRangeErrorShown = false;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.setGlobalTheme(this);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.setAndEmitInitialValue();
  }

  /**
   * If an initial value exists, set it to the local state variables here,
   * and emit if configured to do so
   */
  setAndEmitInitialValue() {
    if (this.initialValue) {
      const { from, to } = this.initialValue;
      this._toDate = to;
      this._fromDate = from;

      if (this.emitInitialValue) {
        this.valueChange.emit(this.getRange());
      }
    }
  }

  /**
   * Get the range for emission, taking into account any configured fromDataPath
   * and toDataPath.
   */
  getRange() {
    return {
      [this.fromDataPath]: this._fromDate,
      [this.toDataPath]: this._toDate,
    };
  }

  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() reset() {
    this._invalidRangeErrorShown = false;
    return Promise.all([
      this._fromElement.reset(true),
      this._toElement.reset(true),
    ]);
  }

  // ================================================================ //
  // -- Local Methods

  handleRangeChange(
    range: string,
    valueChangeEvent: CustomEvent<FluidFormElementState>
  ) {
    EventUtils.stopEvent(valueChangeEvent);
    this[range === this.toDataPath ? '_toDate' : '_fromDate'] =
      valueChangeEvent.detail.value;
    if (this.isValidDateRange()) {
      this._invalidRangeErrorShown = false;
      this.valueChange.emit(this.getRange());
    } else {
      this._invalidRangeErrorShown = true;
    }
  }

  /**
   * Checks if the range is a valid one
   */
  isValidDateRange() {
    if (!this._toDate || !this._fromDate) {
      return true;
    }
    return isAfter(
      parseISO(this._toDate as string),
      parseISO(this._fromDate as string)
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div class={`fluid-date-range theme-${this.theme}`}>
        <fluid-grid>
          <DateRangeField
            setRef={(fromElement: HTMLFluidDatepickerElement) =>
              (this._fromElement = fromElement)
            }
            handleRangeChange={this.handleRangeChange.bind(this)}
            label={this.fromLabel}
            dataPath={this.fromDataPath}
            initialValue={this.initialValue?.from}
            fluidTranslate={this.fluidTranslate?.minDate}
            minDate={this.minDate}
            maxDate={this.maxDate}
            identifier={`${this.identifier}_date_range_from_field`}
          />
          <DateRangeField
            setRef={(toElement: HTMLFluidDatepickerElement) =>
              (this._toElement = toElement)
            }
            handleRangeChange={this.handleRangeChange.bind(this)}
            label={this.toLabel}
            disabled={!this._fromDate}
            dataPath={this.toDataPath}
            initialValue={this.initialValue?.to}
            fluidTranslate={this.fluidTranslate?.maxDate}
            minDate={this._fromDate ? this._fromDate : this.minDate}
            maxDate={this.maxDate}
            identifier={`${this.identifier}_date_range_to_field`}
          />
          <InvalidDateMessage
            doRender={this._invalidRangeErrorShown}
            i18nStore={this._i18nStore}
            fluidTranslate={this.fluidTranslate?.invalidRange}
          />
        </fluid-grid>
      </div>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: {
    minDate?: FluidFormElementTranslationOptions;
    maxDate?: FluidFormElementTranslationOptions;
    invalidRange?: string;
  };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;

  /**
   * @internal
   * Used by internal components to determine if the initial value should
   * be emitted as value change (for date based filters, etc)
   */
  @Prop({ mutable: true }) emitInitialValue: boolean;
}
